import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { useGetCurrentUser, getGetCurrentUserQueryKey } from "@workspace/api-client-react";
import { useAuth } from "@clerk/react";

interface LiteModeContextValue {
  isLite: boolean;
}

const LiteModeContext = createContext<LiteModeContextValue>({ isLite: true });

const MODE_SUFFIX = "uiMode";
const HINT_SUFFIX = "advancedHintDismissed";

function storageKey(uid: string | null | undefined, suffix: string) {
  return `plateOS:${uid ?? "anon"}:${suffix}`;
}

function readCachedMode(uid: string | null | undefined): string | null {
  try {
    return localStorage.getItem(storageKey(uid, MODE_SUFFIX));
  } catch {
    return null;
  }
}

function writeCachedMode(uid: string | null | undefined, mode: string) {
  try {
    localStorage.setItem(storageKey(uid, MODE_SUFFIX), mode);
  } catch {
    /* localStorage unavailable — ignore */
  }
}

export function LiteModeProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded, userId } = useAuth();
  const { data: user } = useGetCurrentUser({
    query: { enabled: isLoaded && !!isSignedIn, queryKey: getGetCurrentUserQueryKey() },
  });

  // Persist the resolved mode keyed by Clerk user id so a returning visit (on
  // this device) renders the correct view immediately — before the user query
  // resolves — instead of flashing focused mode for advanced users.
  useEffect(() => {
    if (user?.uiMode) writeCachedMode(userId, user.uiMode);
  }, [userId, user?.uiMode]);

  // Prefer the freshly loaded account value; fall back to the per-user cache
  // while it loads. On a brand-new device the cache is empty and we default to
  // focused mode, then snap to the account value once it arrives.
  const resolvedMode = user?.uiMode ?? readCachedMode(userId);
  const isLite = resolvedMode !== "advanced";

  return (
    <LiteModeContext.Provider value={{ isLite }}>
      {children}
    </LiteModeContext.Provider>
  );
}

export function useLiteMode() {
  return useContext(LiteModeContext);
}

/**
 * One-time, dismissible hint that points focused-mode users to where the
 * advanced view lives ("See full breakdown" / "More tools"). Dismissal is
 * remembered per user in localStorage so it only ever shows once.
 */
export function useAdvancedHint() {
  const { userId } = useAuth();
  const { isLite } = useLiteMode();
  // Start hidden so we never flash the hint before reading the dismissed flag.
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(storageKey(userId, HINT_SUFFIX)) === "1");
    } catch {
      setDismissed(true);
    }
  }, [userId]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(storageKey(userId, HINT_SUFFIX), "1");
    } catch {
      /* localStorage unavailable — ignore */
    }
    setDismissed(true);
  }, [userId]);

  return { show: isLite && !dismissed, dismiss };
}
